import React, { useRef, useState } from "react";
import { 
	RCCButton, 
	RCCGrid, 
	RCCTooltip,  
	RCCDropDownItem, 
	RCCIcon,
	RCCInput,
	RCCBadge
} from "react-common-components";
import { IForwardPagination } from "react-common-components/dist/components/Pagination/interface";
import { useForm } from "react-hook-form";
import { useTranslation } from "src/context/Translations/context";
import Pagination from "src/components/Pagination";
import FiltersContainer from "src/components/FiltersContainer";
import PageTitle from "src/components/PageTitle";
import OptionsColumn from "src/components/DefaultColumnsList/Options";
import Select from "src/components/Select";
import { useAuth } from "src/context/Auth/context";
import AppIcons from "src/components/AppIcons";
import ReportCrudModal from "./components/reportModal";
import DeleteModalReport from "./deleteModalReport";
import { IReportListFilters, IRefDeleteReportModal, IRefReportModal } from "./interface";


const ReportsList: React.FC = () => { 
	const {administrator,superUser} = useAuth();
	const { translate } = useTranslation();
	const refModal = useRef<IRefReportModal>(null);
	const refModalDelete = useRef<IRefDeleteReportModal>(null);
	const paginationRef = useRef<IForwardPagination>(null);
	const [showFilters, setShowFilters] = useState(false);
	const [filters, setFilters] = useState<IReportListFilters>({filter:"",active:"S" });
	
	const { 
		handleSubmit : handleSubmitFilters, 
		register : registerFilters,
		control:controlFilters, 
		reset: resetFilters, 
		watch: watchFilters 
	} = useForm();

	const submitFilters = (data: any) => {
		setFilters(() => {
			const newState = {} as IReportListFilters;
			newState.filter = data.filter;
			if(data.active)
				newState.active = typeof data.active === "object" ? data.active?.value : data.active;

			return newState;
		});
		paginationRef.current?.reload();
	};

	const colSituacao = (row: any) => (
		<RCCBadge color={row.wda002Ativo ? "green" : "red"}
			styleInline="font-size: 0.83rem;"
		>
			{row.wda002Ativo ? translate("Ativo") : translate("Inativo")}
		</RCCBadge>
	);

	const areUsingFilter = ():boolean => !!filters.filter || filters.active !== "T";

	const colOptions = (row: any) => (
		<>
			{(administrator || superUser) &&
			<OptionsColumn>
				
				<RCCDropDownItem
					selfEnclose
					onClick={() => { refModal?.current?.actionButtonChange(row.wda002Id,false,() => paginationRef.current?.reload()); }}
				>
					<RCCIcon 
						name="pencil" 
					/>
					{translate("Alterar")}
				</RCCDropDownItem>
				<RCCDropDownItem 
					selfEnclose
					onClick={() => { refModal?.current?.actionButtonChange(row.wda002Id,true,() => paginationRef.current?.reload());}}
				>
					<RCCIcon 
						name="copy" 
					/>
					{translate("Copiar")}
				</RCCDropDownItem>
				<RCCDropDownItem 
					selfEnclose
					onClick={() => { refModalDelete.current?.actionButtonDelete(row,() => paginationRef.current?.reload());}}
				>
					<RCCIcon 
						name="trash" 
					/>
					{translate("Excluir")}
				</RCCDropDownItem>
			</OptionsColumn>
			}
		</>
	);

	return (
		<>
			<RCCGrid
				container
				item
				direction="column"
			>
				<PageTitle
					title={translate("Relatórios")}
					icon={() => <AppIcons name="reports" />}
				>
					<RCCTooltip
						text={translate("Novo relatório")}
						color="purple"
					>
						<RCCButton
							icon={{name: "plus", color: "#FFF", size: 18}}
							color="purple"
							layout="isRounded"
							onClick={
								() => { refModal?.current?.actionButtonAdd(() => paginationRef.current?.reload());}
							}
							size="md"
						/>
					</RCCTooltip>
					<RCCTooltip
						text={translate("Filtros")}
						color={showFilters || areUsingFilter() ? "blue" : "grey"}
					>
						<RCCButton
							icon={{name: "filter", color: "#FFF", size: 16}}
							color={showFilters || areUsingFilter() ? "blue" : "grey"}
							layout="isRounded"
							size="md"
							onClick={() => setShowFilters((prevState) => !prevState)}
						/>
					</RCCTooltip>
				</PageTitle>

				<FiltersContainer
					onSubmitFilters={handleSubmitFilters(submitFilters)}
					resetFilters={
						() => resetFilters({
							filter: "",
							active:{value: "S", label: translate("Ativo")}
						})}
					showFilters={showFilters}
				>
					<RCCGrid
						container
						item
					>
						<RCCGrid
							container
							item
							direction="row"
							spacing={1}
						>
							<RCCGrid
								container 
								item
								xs={12}
								sm={4}
								md={2}
							>
								<Select 
									control={controlFilters}
									name="active" 
									options={[
										{value: "S", label: translate("Ativo")}, 
										{value: "N", label: translate("Inativo")},
										{value: "T", label: translate("Todos")}
									]} 
									label={translate("Situação")}
									defaultValue={{value: "S", label: translate("Ativo")}}
									submit="value and label"
								/>
							</RCCGrid>
							<RCCGrid
								container 
								item
								xs={12}
								sm={8}
								md={4}
							>
								<RCCInput
									name="filter" 
									label={translate("Pesquisar")}
									register={registerFilters}
									defaultValue={watchFilters("filter")}
								/>
							</RCCGrid>
						</RCCGrid>
					</RCCGrid>
				</FiltersContainer>

				<Pagination 
					ref={paginationRef}
					dataOrRoute="Reports/List"
					filtersData={filters}    
					columns={[
						{
							idColumn: "wda002Titulo",
							nameColumn: translate("Título"),
							sm: 4,
							md: 4,
							type: "string"
						},{
							idColumn: "wda002Tituloabrev",
							nameColumn: translate("Título abreviado"),
							sm: 2,
							md: 2,
							type: "string"
						},
						{
							idColumn: "wda002Agrupador",
							nameColumn: translate("Agrupador"),
							sm: 2,
							md: 2,
							type: "string"
						},
						{
							idColumn: "situation",
							nameColumn: translate("Situação"),
							sm: 1,
							md: 1,
							cellRenderer:colSituacao
						},
						{
							idColumn: "options",
							nameColumn: " ",
							justify: "right",
							sm: 2,
							md: 3,
							cellRenderer: colOptions,
							disableSort: true
						}
					]}
					styleInline={{
						ulContainer: "padding-top: 20px; background-color: var(--input-background-color);"
					}}
				/>
			</RCCGrid>
			<ReportCrudModal ref={refModal}/> 
			<DeleteModalReport ref={refModalDelete} />
		</>
	);
};

export default ReportsList;
